/* kic-v2-pdp.js - PDP v3 scoped behavior. No external deps. No innerHTML.
   Priority 1 re-work 2026-04-24: block-differentiated motion
     - Hero primary image: scroll-parallax vertical (~10% travel)
     - Hero buybox: scale-drop on DOMContentLoaded (no scroll trigger)
     - Block 3 THE SHOP photo: scroll-synced horizontal pan (~3%)
     - Block 7 spec rows: stagger-stamp on IO enter (rotation + slide + fade)
     - Block 10 recs title: existing kic-hot letter-stagger preserved
   Priority 2 re-work 2026-04-24: 5 new distinct motion primitives
     - Block 2 piece: clip-path wipe reveal on prose paragraphs (stagger)
     - Block 4 grading card: scale-drop + per-char stamp scale/rotate on abbr
     - Block 5 provenance stamps: overshoot drop-in w/ rotation per stamp
     - Block 6 wax seal: SVG stroke-dashoffset draw-on + filter drop-shadow build
     - Block 9 FAQ: spring-open accordion (cubic-bezier overshoot on max-height)
     - Mobile ticker: scroll-synced active pill (<1200px) via IO on sections
*/
(function () {
  'use strict';

  var root = document.querySelector('.kv-pdp');
  if (!root) return;

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Reveal on scroll (IntersectionObserver) --- */
  if (!prefersReduced && 'IntersectionObserver' in window) {
    var revealObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05 });
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      revealObs.observe(el);
    });

    /* Stagger-stamp observer for Block 7 spec rows (distinct primitive from data-reveal). */
    var stampObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var rows = entry.target.parentNode ? entry.target.parentNode.querySelectorAll('[data-stamp-row]') : [entry.target];
        var target = entry.target;
        // find index among siblings for cascading delay
        var siblings = target.parentNode ? target.parentNode.querySelectorAll('[data-stamp-row]') : null;
        var idx = 0;
        if (siblings) {
          for (var s = 0; s < siblings.length; s++) {
            if (siblings[s] === target) { idx = s; break; }
          }
        }
        target.style.transitionDelay = (idx * 80) + 'ms';
        target.classList.add('is-stamped');
        stampObs.unobserve(target);
      });
    }, { threshold: 0.15 });
    root.querySelectorAll('[data-stamp-row]').forEach(function (el) {
      stampObs.observe(el);
    });

    /* Force reveal of any [data-reveal] elements already in (or above) the initial viewport
       at load, because IntersectionObserver does not fire for elements already intersecting
       when the observer registers. */
    var initialPass = function () {
      var revealEls = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = 0; i < revealEls.length; i++) {
        var el = revealEls[i];
        var rect = el.getBoundingClientRect();
        if (rect.top < vh) {
          el.classList.add('is-revealed');
        }
      }
      /* Stamp rows: same initial pass, cascaded delay by in-sibling index. */
      var stampEls = document.querySelectorAll('[data-stamp-row]:not(.is-stamped)');
      for (var j = 0; j < stampEls.length; j++) {
        var sel = stampEls[j];
        var srect = sel.getBoundingClientRect();
        if (srect.top < vh) {
          /* find sibling index */
          var sibs = sel.parentNode ? sel.parentNode.querySelectorAll('[data-stamp-row]') : null;
          var sidx = 0;
          if (sibs) {
            for (var k = 0; k < sibs.length; k++) {
              if (sibs[k] === sel) { sidx = k; break; }
            }
          }
          sel.style.transitionDelay = (sidx * 80) + 'ms';
          sel.classList.add('is-stamped');
        }
      }
    };
    requestAnimationFrame(function () { requestAnimationFrame(initialPass); });
  } else {
    /* prefers-reduced-motion OR no IO support: reveal everything immediately. */
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
    root.querySelectorAll('[data-stamp-row]').forEach(function (el) {
      el.classList.add('is-stamped');
    });
  }

  /* --- Buybox scale-drop on load (distinct from scroll-reveal primitive) ---
     CLS-4 fix (next theme wave 2026-05-08): the prior 2x rAF chain ran the
     reveal animation inside the post-LCP paint window (+160ms) and
     co-contributed to the CLS-3 buybox-shift event. Idle-defer the reveal
     so it fires after the load critical path is clear; preserves Tier 1
     "motion is encouraged" intent (motion still plays) while removing the
     CLS contribution. prefers-reduced-motion path is unchanged. */
  var dropEls = root.querySelectorAll('[data-scale-drop]');
  if (dropEls.length) {
    if (prefersReduced) {
      dropEls.forEach(function (el) { el.classList.add('is-dropped'); });
    } else {
      var runDrop = function () {
        dropEls.forEach(function (el, i) {
          setTimeout(function () { el.classList.add('is-dropped'); }, i * 80);
        });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(runDrop, { timeout: 1000 });
      } else {
        setTimeout(runDrop, 200);
      }
    }
  }

  /* --- Drop Theater title letter split (Block 10 kic-hot) --- */
  var titleEl = root.querySelector('.kv-pdp__recs-title');
  if (titleEl && !prefersReduced) {
    var txt = titleEl.textContent;
    while (titleEl.firstChild) {
      titleEl.removeChild(titleEl.firstChild);
    }
    for (var i = 0; i < txt.length; i++) {
      var ch = txt.charAt(i);
      if (ch === ' ') {
        titleEl.appendChild(document.createTextNode(' '));
        continue;
      }
      var span = document.createElement('span');
      span.className = 'letter';
      span.style.animationDelay = (i * 40) + 'ms';
      span.textContent = ch;
      titleEl.appendChild(span);
    }
  }

  /* --- Gallery swap --- */
  var primary = root.querySelector('.kv-pdp__gallery-primary img');
  var thumbs = root.querySelectorAll('.kv-pdp__gallery-thumbs button, .kv-pdp__thumb');
  thumbs.forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!primary) return;
      var src = btn.getAttribute('data-full-src');
      if (!src) return;
      primary.classList.add('is-swapping');
      setTimeout(function () {
        primary.removeAttribute('srcset');
        primary.removeAttribute('sizes');
        primary.setAttribute('src', src);
        primary.classList.remove('is-swapping');
      }, 280);
      thumbs.forEach(function (b) {
        b.classList.remove('is-active');
        // Resolve-bundle 2026-05-08 evening (a11y SERIOUS #6): keep
        // aria-current in sync with the visual is-active state. Mirror
        // the aria-pressed update pattern below if/when added.
        b.removeAttribute('aria-current');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
    });
  });

  /* --- Condition chip click-to-expand (grading card v3 Priority 2) --- */
  var condMap = {};
  var condDataEl = root.querySelector('[data-condition-map]');
  if (condDataEl) {
    try { condMap = JSON.parse(condDataEl.textContent); } catch (e) { condMap = {}; }
  }
  var condChips = root.querySelectorAll('.kv-pdp__condition-chip');
  var condDesc = root.querySelector('[data-grading-note]') || root.querySelector('.kv-pdp__condition-desc');
  var condGradeLabel = root.querySelector('[data-grading-label]');
  condChips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      var handle = chip.getAttribute('data-condition-handle');
      if (!handle) return;
      var next = condMap[handle];
      if (condDesc && next) {
        condDesc.classList.add('is-swapping');
        setTimeout(function () {
          condDesc.textContent = next;
          condDesc.classList.remove('is-swapping');
        }, 300);
      }
      /* Swap the large label text too */
      if (condGradeLabel) {
        var newLabel = chip.getAttribute('data-grade-label');
        if (newLabel) condGradeLabel.textContent = newLabel;
      }
      /* Toggle active state on scale bars */
      condChips.forEach(function (c) {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
    });
  });

  /* --- Block 5 stamp-strip overshoot drop-in (distinct primitive: overshoot ease
     with per-stamp rotation). Uses its own observer to cascade 120ms per stamp. --- */
  if (!prefersReduced && 'IntersectionObserver' in window) {
    var stampStripObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var stamps = entry.target.querySelectorAll('[data-stamp]');
        for (var s = 0; s < stamps.length; s++) {
          (function (el, i) {
            setTimeout(function () { el.classList.add('is-stamped'); }, i * 120);
          })(stamps[s], s);
        }
        stampStripObs.unobserve(entry.target);
      });
    }, { threshold: 0.2 });
    var stampStrips = root.querySelectorAll('.kv-pdp__stamp-strip');
    stampStrips.forEach(function (strip) { stampStripObs.observe(strip); });
  } else {
    root.querySelectorAll('[data-stamp]').forEach(function (el) {
      el.classList.add('is-stamped');
    });
  }

  /* --- Block 2 piece prose clip-path wipe reveal. Distinct primitive: clip-path
     inset wipe left-to-right with 150ms stagger per paragraph. --- */
  if (!prefersReduced && 'IntersectionObserver' in window) {
    var wipeObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-wiped');
        wipeObs.unobserve(entry.target);
      });
    }, { threshold: 0.1 });
    root.querySelectorAll('[data-piece-wipe]').forEach(function (el) {
      wipeObs.observe(el);
    });
    /* Initial-viewport pass */
    requestAnimationFrame(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      root.querySelectorAll('[data-piece-wipe]:not(.is-wiped)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < vh) el.classList.add('is-wiped');
      });
    });
  } else {
    root.querySelectorAll('[data-piece-wipe]').forEach(function (el) {
      el.classList.add('is-wiped');
    });
  }

  /* --- Mobile ticker scroll-sync. Active pill reflects current section via IO.
     Active pill centers itself via scrollIntoView with inline:center. --- */
  var ticker = root.querySelector('[data-pdp-mobile-ticker]');
  if (ticker && 'IntersectionObserver' in window) {
    var tickerLinks = ticker.querySelectorAll('a[data-ticker-target]');
    var tickerByTarget = {};
    tickerLinks.forEach(function (a) {
      tickerByTarget[a.getAttribute('data-ticker-target')] = a;
    });
    var tickerObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var a = tickerByTarget[entry.target.id];
        if (!a) return;
        tickerLinks.forEach(function (x) { x.classList.remove('is-active'); });
        a.classList.add('is-active');
        /* Center the active pill in the horizontal ticker */
        try {
          var ul = ticker.querySelector('ul');
          if (ul) {
            var aRect = a.getBoundingClientRect();
            var ulRect = ul.getBoundingClientRect();
            var target = (a.offsetLeft + (a.offsetWidth / 2)) - (ul.clientWidth / 2);
            ul.scrollTo({ left: target, behavior: 'smooth' });
          }
        } catch (err) {}
      });
    }, { threshold: 0.4 });
    Object.keys(tickerByTarget).forEach(function (id) {
      var sec = document.getElementById(id);
      if (sec) tickerObs.observe(sec);
    });
  }

  /* --- FAQ accordion --- */
  root.querySelectorAll('.kv-pdp__faq-q').forEach(function (q) {
    q.addEventListener('click', function () {
      var expanded = q.getAttribute('aria-expanded') === 'true';
      q.setAttribute('aria-expanded', String(!expanded));
    });
  });

  /* --- Anchor nav IntersectionObserver --- */
  var navLinks = root.querySelectorAll('.kv-pdp__anchor-nav a');
  if (navLinks.length && 'IntersectionObserver' in window) {
    var byId = {};
    navLinks.forEach(function (a) {
      var id = a.getAttribute('href').replace(/^#/, '');
      byId[id] = a;
    });
    var navObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var a = byId[entry.target.id];
        if (!a) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          navLinks.forEach(function (x) { x.classList.remove('is-active'); });
          a.classList.add('is-active');
        }
      });
    }, { threshold: [0.5] });
    Object.keys(byId).forEach(function (id) {
      var el = document.getElementById(id);
      if (el) navObs.observe(el);
    });
  }

  /* --- Mobile sticky ATC pill ---
     Wave A 2026-05-01: superseded by kic-pdp-sticky-atc.js (sentinel-based
     reveal + scroll-up hide + thumb/title/price context). Old hero-IO logic
     removed to avoid two controllers fighting over .is-visible. */

  /* --- Wave B 2026-05-01: Gallery thumb roving-tabindex keyboard nav.
     Thumbs are now [data-gallery-thumbs] > li > button[aria-pressed].
     Arrow Left/Right cycle focus, Home/End jump to ends, Enter/Space
     activate the click handler (which already owns full-image swap). */
  var galleryList = root.querySelector('[data-gallery-thumbs]');
  if (galleryList) {
    var thumbButtons = galleryList.querySelectorAll('.kv-pdp__thumb');
    if (thumbButtons.length > 0) {
      // Update aria-pressed + tabindex on click activation.
      thumbButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          thumbButtons.forEach(function (b) {
            b.setAttribute('aria-pressed', 'false');
            b.setAttribute('tabindex', '-1');
          });
          btn.setAttribute('aria-pressed', 'true');
          btn.setAttribute('tabindex', '0');
        });
      });
      // Roving tabindex: arrow keys move focus between thumbs.
      galleryList.addEventListener('keydown', function (ev) {
        var key = ev.key;
        if (key !== 'ArrowLeft' && key !== 'ArrowRight' &&
            key !== 'ArrowUp' && key !== 'ArrowDown' &&
            key !== 'Home' && key !== 'End') {
          return;
        }
        var current = document.activeElement;
        if (!current || !current.classList ||
            !current.classList.contains('kv-pdp__thumb')) {
          return;
        }
        var idx = -1;
        for (var i = 0; i < thumbButtons.length; i++) {
          if (thumbButtons[i] === current) { idx = i; break; }
        }
        if (idx < 0) return;
        ev.preventDefault();
        var nextIdx = idx;
        if (key === 'ArrowLeft' || key === 'ArrowUp') {
          nextIdx = idx === 0 ? thumbButtons.length - 1 : idx - 1;
        } else if (key === 'ArrowRight' || key === 'ArrowDown') {
          nextIdx = idx === thumbButtons.length - 1 ? 0 : idx + 1;
        } else if (key === 'Home') {
          nextIdx = 0;
        } else if (key === 'End') {
          nextIdx = thumbButtons.length - 1;
        }
        thumbButtons[idx].setAttribute('tabindex', '-1');
        thumbButtons[nextIdx].setAttribute('tabindex', '0');
        thumbButtons[nextIdx].focus();
      });
    }
  }
})();
